/**
 * ReferenceFormFields.js
 *
 * Shared form body used by both NewReference and EditReference.
 * Receives all formik helpers + lookup lists as props so it is
 * completely stateless and easily testable.
 */
import React from "react";
import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
  Switch,
  FormGroup,
  MenuItem,
  Select,
  Box,
  Divider,
  Typography,
  useTheme,
  Stack,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DateField, LocalizationProvider } from "@mui/x-date-pickers";
import { Field } from "formik";
import moment from "moment";
import { Status } from "app/DataSource";
import useAxiosPrivate from "app/hooks/useAxiosPrivate";

// ─── Helpers ────────────────────────────────────────────────────────────────

const opt = (v) => (Boolean(v) ? v : { value: "", title: "" });
const acOpts = (list) => (list.error ? [] : list.data ?? []);

/** Thin wrapper so every Autocomplete field looks identical */
function AcField({ name, label, options, value, onChange, onBlur, readOnly, sx, ...rest }) {
  return (
    <Field
      name={name}
      component={Autocomplete}
      options={options}
      getOptionLabel={(o) => o?.title || ""}
      value={opt(value)}
      disableClearable
      autoComplete
      autoHighlight
      readOnly={readOnly}
      onBlur={onBlur}
      onChange={onChange}
      sx={{ minWidth: 140, ...sx }}
      renderInput={(params) => (
        <TextField {...params} name={name} label={label} variant="standard" size="small" />
      )}
      {...rest}
    />
  );
}

/** Section heading with a subtle left-border accent */
function SectionHeading({ children }) {
  const theme = useTheme();
  return (
    <Typography
      variant="subtitle2"
      sx={{
        mt: 2.5,
        mb: 1,
        pl: 1,
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        color: theme.palette.text.secondary,
        fontWeight: 700,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        fontSize: "0.7rem",
      }}
    >
      {children}
    </Typography>
  );
}

/** Wraps a group of fields in a responsive flexbox row */
function FieldRow({ children }) {
  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      gap={2}
      alignItems="flex-end"
      sx={{ mb: 1 }}
    >
      {children}
    </Stack>
  );
}

// ─── Main export ─────────────────────────────────────────────────────────────

export default function ReferenceFormFields({
  // formik
  values,
  handleChange,
  handleBlur,
  setFieldValue,
  touched,
  errors,
  // control
  readOnly = false,
  // lookup lists
  CategoryList,
  PurposeList,
  SalutationList,
  CasteList,
  CasteCategoryList,
  VillageList,
  TehsilList,
  DistrictList,
  StateList,
  CountryList,
  ProfessionList,
  WorkTypeList,
  RefList,
  // edit-only
  UsersList,
  showAssignedTo = false,
  showIsProspect = false,
}) {
  const axiosPrivate = useAxiosPrivate();
  useTheme(); // keep theme context available to styled children
  const [isBusinessAddress, setIsBusinessAddress] = React.useState(false);

  // ── PIN lookup ─────────────────────────────────────────────────────────────
  const lookupPIN = (pin, suffix = "") => {
    if (pin.length !== 6) return;
    axiosPrivate.post("/CheckPINForCustomer.php", { PIN: pin }).then((res) => {
      const result = res.data;
      if (result.status === 1 && result.App.length > 0) {
        const d = result.Data[0];
        ["Tehsil", "District", "State", "Country"].forEach((f) =>
          setFieldValue(`${f}${suffix}`, { value: d[f], title: d[f] }, true)
        );
      }
    }).catch(console.error);
  };

  const lookupVillage = (village, suffix = "") => {
    axiosPrivate.post("/CheckVillageForCustomer.php", { village }).then((res) => {
      const result = res.data;
      if (result.status === 1 && result.App.length > 0) {
        const d = result.Data[0];
        ["Tehsil", "District", "State", "Country"].forEach((f) =>
          setFieldValue(`${f}${suffix}`, { value: d[f], title: d[f] }, true)
        );
        setFieldValue(`PIN${suffix}`, d.PIN, true);
      }
    }).catch(console.error);
  };

  // ── grouped Users (edit-only) ──────────────────────────────────────────────
  const groupedUsers = React.useMemo(() => {
    if (!UsersList) return [];
    const raw = UsersList.error ? [] : UsersList.data ?? [];
    return raw
      .filter((o) => o && o.title)
      .map((o) => ({
        ...o,
        firstLetter: /[0-9]/.test(o.title[0]) ? "0-9" : o.title[0].toUpperCase(),
      }))
      .sort((a, b) => -b.firstLetter.localeCompare(a.firstLetter));
  }, [UsersList]);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* ── Category ─────────────────────────────────────────────────────── */}
      <SectionHeading>Category</SectionHeading>
      <FieldRow>
        <AcField
          name="Category"
          label="NB Form Status"
          options={acOpts(CategoryList)}
          value={values.Category}
          readOnly={readOnly}
          onBlur={handleBlur}
          onChange={(_, v) => setFieldValue("Category", v || { value: "", title: "" })}
        />

        <FormControl variant="standard" sx={{ minWidth: 120 }}>
          <InputLabel id="status-label">Status</InputLabel>
          <Select
            labelId="status-label"
            name="Status"
            value={values?.Status || ""}
            onChange={handleChange}
            inputProps={{ readOnly }}
            error={touched.Status && Boolean(errors.Status)}
          >
            {[...Status, "POLICY SERVICE", "PENDING", "PREMIUM POINT"].map((n) => (
              <MenuItem key={n} value={n}>{n}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <AcField
          name="Purpose"
          label="Purpose"
          options={acOpts(PurposeList)}
          value={values.Purpose}
          readOnly={readOnly}
          onBlur={handleBlur}
          onChange={(_, v) => setFieldValue("Purpose", v || { value: "", title: "" })}
        />

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateField
            readOnly={readOnly}
            label="Date of Birth"
            variant="standard"
            size="small"
            name="DOB"
            format="dd-MM-yyyy"
            value={values.DOB ?? null}
            onChange={(v) => setFieldValue("DOB", v, true)}
            onBlur={handleBlur}
            sx={{ width: 130 }}
          />
        </LocalizationProvider>

        {values.DOB && (
          <Typography variant="caption" sx={{ color: "text.secondary", alignSelf: "flex-end" }}>
            {moment().diff(new Date(values.DOB), "years")} yr(s)
          </Typography>
        )}
      </FieldRow>

      {/* ── Visit / Ref details ───────────────────────────────────────────── */}
      <SectionHeading>Visit / Ref Details</SectionHeading>
      <FieldRow>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateField
            readOnly={readOnly}
            label="Date"
            variant="standard"
            size="small"
            name="Date"
            format="dd-MM-yyyy"
            value={values.Date ?? null}
            onChange={(v) => setFieldValue("Date", v, true)}
            onBlur={handleBlur}
            sx={{ width: 130 }}
          />
        </LocalizationProvider>

        <TextField
          label="Place"
          size="small"
          variant="standard"
          name="Place"
          value={values.Place ?? ""}
          onChange={handleChange}
          onBlur={handleBlur}
          InputProps={{ readOnly }}
          error={touched.Place && Boolean(errors.Place)}
          helperText={touched.Place && errors.Place}
          sx={{ width: 160 }}
        />

        <AcField
          name="RefBy"
          label="Referred By"
          options={acOpts(RefList)}
          value={values.RefBy}
          readOnly={readOnly}
          onBlur={handleBlur}
          onChange={(_, v) => {
            setFieldValue("RefBy", v || { value: "", title: "" });
            setFieldValue("RefByID", v?.ID ?? "");
          }}
          sx={{ width: 200 }}
        />

        {(values.RefBy?.value === "CUSTOMER" || values.RefBy?.value === "OTHER") && (
          <TextField
            label={`${values.RefBy.value === "CUSTOMER" ? "Customer" : "Ref By"} Name`}
            size="small"
            variant="standard"
            name="RefByName"
            value={values.RefByName ?? ""}
            onChange={handleChange}
            onBlur={handleBlur}
            InputProps={{ readOnly }}
            error={touched.RefByName && Boolean(errors.RefByName)}
            helperText={touched.RefByName && errors.RefByName}
            sx={{ width: 180 }}
          />
        )}
      </FieldRow>

      {/* ── Personal details ──────────────────────────────────────────────── */}
      <SectionHeading>Personal Details</SectionHeading>
      <FieldRow>
        <AcField
          name="Salutation"
          label="Salutation"
          options={acOpts(SalutationList)}
          value={values.Salutation}
          readOnly={readOnly}
          onBlur={handleBlur}
          onChange={(_, v) => setFieldValue("Salutation", v || { value: "", title: "" })}
          sx={{ width: 110 }}
        />

        <TextField
          label="Full Name"
          size="small"
          variant="standard"
          name="Name"
          value={values.Name ?? ""}
          onChange={handleChange}
          onBlur={handleBlur}
          InputProps={{ readOnly }}
          error={touched.Name && Boolean(errors.Name)}
          helperText={touched.Name && errors.Name}
          sx={{ width: 220 }}
        />

        <AcField
          name="Caste"
          label="Caste"
          options={acOpts(CasteList)}
          value={values.Caste}
          readOnly={readOnly}
          onBlur={handleBlur}
          onChange={(_, v) => setFieldValue("Caste", v || { value: "", title: "" })}
          sx={{ width: 140 }}
        />

        <AcField
          name="CasteCategory"
          label="Caste Category"
          options={acOpts(CasteCategoryList)}
          value={values.CasteCategory}
          readOnly={readOnly}
          onBlur={handleBlur}
          onChange={(_, v) => setFieldValue("CasteCategory", v || { value: "", title: "" })}
          sx={{ width: 150 }}
        />
      </FieldRow>

      <FieldRow>
        <FormControl>
          <FormLabel sx={{ fontSize: "0.75rem" }}>Gender</FormLabel>
          <RadioGroup row name="Gender" value={values.Gender ?? ""} onChange={handleChange}>
            {["Male", "Female"].map((g) => (
              <FormControlLabel
                key={g}
                value={g}
                label={g}
                control={<Radio size="small" disabled={readOnly} />}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <FormControl variant="standard" sx={{ minWidth: 130 }}>
          <InputLabel shrink={Boolean(values.MaritalStatus)}>Marital Status</InputLabel>
          <Select
            name="MaritalStatus"
            value={values?.MaritalStatus || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            inputProps={{ readOnly }}
          >
            {["Single", "Married", "Divorced", "Widowed"].map((v) => (
              <MenuItem key={v} value={v}>{v}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </FieldRow>

      {/* ── Contact details ───────────────────────────────────────────────── */}
      <SectionHeading>Contact Details</SectionHeading>

      {/* Address type toggle */}
      <FormControl component="fieldset" variant="standard" sx={{ mb: 1 }}>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={isBusinessAddress}
                onChange={() => setIsBusinessAddress((p) => !p)}
                disabled={readOnly}
              />
            }
            label={
              <Typography variant="caption">
                {isBusinessAddress ? "Business Address" : "Residential Address"}
              </Typography>
            }
          />
        </FormGroup>
      </FormControl>

      {/* Address block — business or residential */}
      {isBusinessAddress ? (
        <>
          <FieldRow>
            <TextField
              label="Business Address"
              size="small"
              variant="standard"
              name="Address2"
              value={values.Address2 ?? ""}
              onChange={handleChange}
              onBlur={handleBlur}
              InputProps={{ readOnly }}
              InputLabelProps={{ shrink: Boolean(values.Address2 || values.Address) }}
              sx={{ width: 280 }}
            />
            <AcField
              name="Village2"
              label="VPO / City"
              options={acOpts(VillageList)}
              value={values.Village2}
              readOnly={readOnly}
              onBlur={handleBlur}
              onChange={(_, v) => {
                setFieldValue("Village2", v || { value: "", title: "" });
                if (v?.value) lookupVillage(v.value, "2");
              }}
              sx={{ width: 180 }}
            />
            <AcField
              name="Tehsil2"
              label="Tehsil"
              options={acOpts(TehsilList)}
              value={values.Tehsil2}
              readOnly={readOnly}
              onBlur={handleBlur}
              onChange={(_, v) => setFieldValue("Tehsil2", v || { value: "", title: "" })}
              sx={{ width: 170 }}
            />
          </FieldRow>
          <FieldRow>
            <AcField
              name="District2"
              label="District"
              options={acOpts(DistrictList)}
              value={values.District2}
              readOnly={readOnly}
              onBlur={handleBlur}
              onChange={(_, v) => setFieldValue("District2", v || { value: "", title: "" })}
              sx={{ width: 170 }}
            />
            <AcField
              name="State2"
              label="State"
              options={acOpts(StateList)}
              value={values.State2}
              readOnly={readOnly}
              onBlur={handleBlur}
              onChange={(_, v) => setFieldValue("State2", v || { value: "", title: "" })}
              sx={{ width: 170 }}
            />
            <AcField
              name="Country2"
              label="Country"
              options={acOpts(CountryList)}
              value={values.Country2}
              readOnly={readOnly}
              onBlur={handleBlur}
              onChange={(_, v) => setFieldValue("Country2", v || { value: "INDIA", title: "INDIA" })}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              sx={{ width: 120 }}
            />
            <TextField
              label="PIN Code"
              size="small"
              variant="standard"
              name="PIN2"
              value={values.PIN2 ?? ""}
              onChange={(e) => {
                setFieldValue("PIN2", e.target.value, true);
                lookupPIN(e.target.value, "2");
              }}
              onBlur={handleBlur}
              InputProps={{ readOnly }}
              InputLabelProps={{ shrink: Boolean(values.PIN2) }}
              error={touched.PIN2 && Boolean(errors.PIN2)}
              helperText={touched.PIN2 && errors.PIN2}
              sx={{ width: 100 }}
            />
          </FieldRow>
        </>
      ) : (
        <>
          <FieldRow>
            <TextField
              label="Address"
              size="small"
              variant="standard"
              name="Address"
              value={values.Address ?? ""}
              onChange={handleChange}
              onBlur={handleBlur}
              InputProps={{ readOnly }}
              sx={{ width: 280 }}
            />
            <AcField
              name="Village"
              label="VPO / City"
              options={acOpts(VillageList)}
              value={values.Village}
              readOnly={readOnly}
              onBlur={handleBlur}
              onChange={(_, v) => {
                setFieldValue("Village", v || { value: "", title: "" });
                if (v?.value) lookupVillage(v.value, "");
              }}
              sx={{ width: 180 }}
            />
            <AcField
              name="Tehsil"
              label="Tehsil"
              options={acOpts(TehsilList)}
              value={values.Tehsil}
              readOnly={readOnly}
              onBlur={handleBlur}
              onChange={(_, v) => setFieldValue("Tehsil", v || { value: "", title: "" })}
              sx={{ width: 170 }}
            />
          </FieldRow>
          <FieldRow>
            <AcField
              name="District"
              label="District"
              options={acOpts(DistrictList)}
              value={values.District}
              readOnly={readOnly}
              onBlur={handleBlur}
              onChange={(_, v) => setFieldValue("District", v || { value: "", title: "" })}
              sx={{ width: 170 }}
            />
            <AcField
              name="State"
              label="State"
              options={acOpts(StateList)}
              value={values.State}
              readOnly={readOnly}
              onBlur={handleBlur}
              onChange={(_, v) => setFieldValue("State", v || { value: "", title: "" })}
              sx={{ width: 170 }}
            />
            <AcField
              name="Country"
              label="Country"
              options={acOpts(CountryList)}
              value={values.Country}
              readOnly={readOnly}
              onBlur={handleBlur}
              onChange={(_, v) => setFieldValue("Country", v || { value: "INDIA", title: "INDIA" })}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              sx={{ width: 120 }}
            />
            <TextField
              label="PIN Code"
              size="small"
              variant="standard"
              name="PIN"
              value={values.PIN ?? ""}
              onChange={(e) => {
                setFieldValue("PIN", e.target.value, true);
                lookupPIN(e.target.value, "");
              }}
              onBlur={handleBlur}
              InputProps={{ readOnly }}
              InputLabelProps={{ shrink: Boolean(values.PIN) }}
              error={touched.PIN && Boolean(errors.PIN)}
              helperText={touched.PIN && errors.PIN}
              sx={{ width: 100 }}
            />
          </FieldRow>
        </>
      )}

      {/* Phone / Email */}
      <FieldRow>
        <TextField
          label="WhatsApp No."
          size="small"
          variant="standard"
          name="WhatsAppNo"
          value={values.WhatsAppNo ?? ""}
          onChange={handleChange}
          onBlur={handleBlur}
          InputProps={{ readOnly }}
          error={touched.WhatsAppNo && Boolean(errors.WhatsAppNo)}
          helperText={touched.WhatsAppNo && errors.WhatsAppNo}
          sx={{ width: 160 }}
        />
        <TextField
          label="Resi. Mobile No."
          size="small"
          variant="standard"
          name="MobileNo"
          value={values.MobileNo ?? ""}
          onChange={handleChange}
          onBlur={handleBlur}
          InputProps={{ readOnly }}
          error={touched.MobileNo && Boolean(errors.MobileNo)}
          helperText={touched.MobileNo && errors.MobileNo}
          sx={{ width: 160 }}
        />
        <TextField
          label="Email"
          size="small"
          variant="standard"
          name="Email"
          value={values.Email ?? ""}
          onChange={handleChange}
          onBlur={handleBlur}
          InputProps={{ readOnly }}
          error={touched.Email && Boolean(errors.Email)}
          helperText={touched.Email && errors.Email}
          sx={{ width: 200 }}
        />
      </FieldRow>

      {/* ── Work details ──────────────────────────────────────────────────── */}
      <SectionHeading>Work Details</SectionHeading>
      <FieldRow>
        <AcField
          name="Profession"
          label="Profession"
          options={acOpts(ProfessionList)}
          value={values.Profession}
          readOnly={readOnly}
          onBlur={handleBlur}
          onChange={(_, v) => setFieldValue("Profession", v || { value: "", title: "" })}
          sx={{ width: 180 }}
        />
        <AcField
          name="WorkType"
          label="Work Type"
          options={acOpts(WorkTypeList)}
          value={values.WorkType}
          readOnly={readOnly}
          onBlur={handleBlur}
          onChange={(_, v) => setFieldValue("WorkType", v || { value: "", title: "" })}
          sx={{ width: 180 }}
        />
      </FieldRow>

      {/* ── Other details ────────────────────────────────────────────────── */}
      <SectionHeading>Other Details</SectionHeading>
      <FieldRow>
        <TextField
          label="CKYC / ID Number"
          size="small"
          variant="standard"
          name="Other"
          value={values.Other ?? ""}
          onChange={handleChange}
          onBlur={handleBlur}
          InputProps={{ readOnly }}
          error={touched.Other && Boolean(errors.Other)}
          helperText={touched.Other && errors.Other}
          sx={{ width: 200 }}
        />

        {showIsProspect && (
          <FormControl variant="standard" sx={{ minWidth: 120 }}>
            <InputLabel>In Prospect?</InputLabel>
            <Select
              name="IsProspect"
              value={values?.IsProspect || ""}
              onChange={handleChange}
              inputProps={{ readOnly }}
            >
              <MenuItem value="YES">Yes</MenuItem>
              <MenuItem value="NO">No</MenuItem>
            </Select>
          </FormControl>
        )}

        {!showIsProspect && (
          <FormControl variant="standard" sx={{ minWidth: 120 }}>
            <InputLabel>In Prospect?</InputLabel>
            <Select
              name="Prospect"
              value={values?.Prospect || ""}
              onChange={handleChange}
              inputProps={{ readOnly }}
              error={touched.Prospect && Boolean(errors.Prospect)}
            >
              <MenuItem value="Yes">Yes</MenuItem>
              <MenuItem value="No">No</MenuItem>
            </Select>
          </FormControl>
        )}

        {showAssignedTo && (
          <AcField
            name="AssignedTo"
            label="Assigned To"
            options={groupedUsers}
            groupBy={(o) => o.UserType}
            value={values.AssignedTo}
            readOnly={readOnly}
            onBlur={handleBlur}
            onChange={(_, v) => setFieldValue("AssignedTo", v || { value: "", title: "" })}
            sx={{ width: 200 }}
          />
        )}
      </FieldRow>

      <Divider sx={{ mt: 2, opacity: 0.2 }} />
    </Box>
  );
}
